/**
 * FERP Portal - Metadata Repository
 *
 * Repository for primitive and subgraph metadata discovery (DRR-065).
 * Provides type-safe access to backend metadata without tenant requirement.
 *
 * PATTERN: Repository pattern (Layer 2)
 * - Wraps GraphQL queries with audit logging
 * - No tenant ID required (metadata is global)
 * - Read-only operations (no mutations)
 */

// eslint-disable-next-line no-restricted-imports -- Repository definitions require gql tag
import { gql } from '@apollo/client';
// eslint-disable-next-line no-restricted-imports -- Repository requires direct Apollo access for non-tenant queries
import { useQuery } from '@apollo/client/react';
import { useCallback } from 'react';

import { useAuditLogger } from './useAuditLogger';
import type {
  MetadataManifest,
  PrimitiveMetadata,
  SubgraphMetadata,
  EntityTypeMetadata,
  FormPatternMetadata,
} from '@/generated/metadata-manifest';

// ============================================================================
// QUERIES
// ============================================================================

const METADATA_MANIFEST_QUERY = gql`
  query MetadataManifest {
    metadataManifest {
      manifestVersion
      primitives {
        name
        symbol
        graphqlQuery
        graphqlMutationPrefix
        phase
        fields
        registryVersion
        description
        lifeCritical
        metadata
      }
      subgraphs {
        name
        number
        entityTypes {
          name
          code
          graphqlQuery
          safetyLevel
          fields
          graphqlTypeName
          description
          metadata
        }
        description
      }
      formPatterns {
        name
        layoutType
        description
      }
      generatedAt
    }
  }
`;

const PRIMITIVE_QUERY = gql`
  query Primitive($name: String!) {
    primitive(name: $name) {
      name
      symbol
      graphqlQuery
      graphqlMutationPrefix
      phase
      fields
      registryVersion
      description
      lifeCritical
      metadata
    }
  }
`;

const AVAILABLE_PRIMITIVES_QUERY = gql`
  query AvailablePrimitives {
    availablePrimitives {
      name
      symbol
      graphqlQuery
      graphqlMutationPrefix
      phase
      description
      lifeCritical
    }
  }
`;

// ============================================================================
// TYPES
// ============================================================================

export interface MetadataRepository {
  /**
   * Get complete metadata manifest
   * Contains all primitives, subgraphs, and form patterns
   */
  useManifest: () => {
    data: MetadataManifest | null;
    loading: boolean;
    error: Error | undefined;
    refetch: () => void;
  };

  /**
   * Get metadata for a specific primitive
   */
  usePrimitive: (name: string) => {
    data: PrimitiveMetadata | null;
    loading: boolean;
    error: Error | undefined;
    refetch: () => void;
  };

  /**
   * Get all available primitives
   */
  usePrimitives: () => {
    data: PrimitiveMetadata[];
    loading: boolean;
    error: Error | undefined;
    refetch: () => void;
  };
}

// ============================================================================
// REPOSITORY HOOK
// ============================================================================

/**
 * Metadata repository hook
 *
 * Usage:
 * ```tsx
 * const metadata = useMetadataRepository();
 * const { data: manifest } = metadata.useManifest();
 * const { data: primitive } = metadata.usePrimitive('Workflow');
 * ```
 */
export function useMetadataRepository(): MetadataRepository {
  const logger = useAuditLogger('Metadata');

  /**
   * Get complete metadata manifest
   */
  const useManifest = useCallback(() => {
    const trace = logger.trace('get');

    // NOTE: metadataManifest query not yet in supergraph schema - skip to avoid errors
    const result = useQuery<Record<string, unknown>>(METADATA_MANIFEST_QUERY, {
      skip: true,
      fetchPolicy: 'cache-and-network',
    });

    return {
      data: result.data?.metadataManifest || null,
      loading: result.loading,
      error: result.error,
      refetch: result.refetch,
    };
  }, [logger]);

  /**
   * Get metadata for a specific primitive
   */
  const usePrimitive = useCallback(
    (name: string) => {
      const trace = logger.trace('get', { name });

      // NOTE: primitive(name:) query not yet in supergraph schema - skip to avoid errors
      const result = useQuery<Record<string, unknown>>(PRIMITIVE_QUERY, {
        variables: { name },
        skip: true,
        fetchPolicy: 'cache-and-network',
      });

      return {
        data: result.data?.primitive || null,
        loading: result.loading,
        error: result.error,
        refetch: result.refetch,
      };
    },
    [logger]
  );

  /**
   * Get all available primitives
   */
  const usePrimitives = useCallback(() => {
    const trace = logger.trace('list');

    // NOTE: availablePrimitives query not yet in supergraph schema - skip to avoid errors
    const result = useQuery<Record<string, unknown>>(AVAILABLE_PRIMITIVES_QUERY, {
      skip: true,
      fetchPolicy: 'cache-and-network',
    });

    return {
      data: result.data?.availablePrimitives || [],
      loading: result.loading,
      error: result.error,
      refetch: result.refetch,
    };
  }, [logger]);

  return {
    // @ts-expect-error Type assignment
    useManifest,
    // @ts-expect-error Type assignment
    usePrimitive,
    // @ts-expect-error Type assignment
    usePrimitives,
  };
}

export default useMetadataRepository;
